import { ALL_INDIAN_CITIES } from "../data/indianCities";
import { calculateDistance } from "./geolocation";

// UserLocationData shape (field names must stay identical to mobile):
// { state, city, pincode, latitude, longitude }

const STORAGE_KEY = "fusshn_user_location";

export const USER_LOCATION_CHANGED_EVENT = "fusshn:user-location-changed";

/**
 * Single writer for the user's location — persists to localStorage and
 * notifies every consumer (mirrors mobile's setUserLocationInDbAndState).
 * @param {{state: string, city: string, pincode: string, latitude: number, longitude: number}} loc
 */
export function saveUserLocation(loc) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    window.dispatchEvent(new CustomEvent(USER_LOCATION_CHANGED_EVENT, { detail: loc }));
}

/**
 * @returns {{state: string, city: string, pincode: string, latitude: number, longitude: number}|null}
 */
export function loadUserLocation() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
        return migrateLegacyLocation();
    } catch {
        return null;
    }
}

// Older builds stored { name, coordinates } under "selectedCityData"; convert
// once so returning users don't hit the first-visit gate again.
function migrateLegacyLocation() {
    try {
        const raw = localStorage.getItem("selectedCityData");
        if (!raw) return null;
        const legacy = JSON.parse(raw);
        if (!legacy?.name || !legacy?.coordinates) return null;

        const name = legacy.name === "Delhi/NCR" ? "Delhi NCR" : legacy.name;
        const match = ALL_INDIAN_CITIES.find((c) => c.name === name);
        const loc = {
            state: match?.state ?? "",
            city: name,
            pincode: "250110",
            latitude: legacy.coordinates.latitude,
            longitude: legacy.coordinates.longitude,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
        return loc;
    } catch {
        return null;
    }
}

/**
 * Reverse-geocode GPS coordinates into display labels only (BigDataCloud —
 * free, keyless, client-side friendly). Throws if nothing usable comes back.
 * @returns {Promise<{city: string, state: string, pincode: string}>}
 */
export async function reverseGeocode(latitude, longitude) {
    const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
    );
    if (!response.ok) throw new Error("Reverse geocoding request failed");

    const data = await response.json();
    const city = data.city || data.locality || "";
    const state = data.principalSubdivision || "";
    const pincode = data.postcode || "";
    if (!city) throw new Error("Reverse geocoding returned no usable place");

    return { city, state, pincode };
}

/**
 * Fallback when reverse geocoding fails: nearest city from the dataset,
 * used for its labels only — the stored coordinates stay the exact GPS ones.
 */
export function findNearestCity(latitude, longitude) {
    let nearest = ALL_INDIAN_CITIES[0];
    let nearestDistance = Infinity;

    for (const city of ALL_INDIAN_CITIES) {
        const distance = calculateDistance(latitude, longitude, city.latitude, city.longitude);
        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = city;
        }
    }
    return nearest;
}
